
import React, { useEffect, useState } from 'react';
import { SimulationConfig, AppSave, CustomMelody } from '@/types';
import { SectionLabel } from './SectionLabel';
import { CloudService } from '@/services/CloudService';
import { ControlGroup } from './ControlGroup';
import { InfoPopup } from './InfoPopup';
import { STORAGE_KEY, DEFAULT_CONFIG, GLOBAL_USER_SOUNDS_KEY, GLOBAL_CUSTOM_MELODIES_KEY } from '@/constants/constants';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/storage';
import { useSettingLocks } from '@/hooks/useSettingLocks';
import { persistentStore } from '@/lib/LocalPersistentStore';

interface StorageManagerProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  cloudSaves: AppSave[];
  setCloudSaves: React.Dispatch<React.SetStateAction<AppSave[]>>;
  onReset: () => void;
}

export const StorageManager: React.FC<StorageManagerProps> = ({ config, setConfig, cloudSaves, setCloudSaves, onReset }) => {
  const [activeTab, setActiveTab] = useState<'LOCAL' | 'CLOUD'>('LOCAL');
  const [saveName, setSaveName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localSaves, setLocalSaves] = useState<Record<string, { config: SimulationConfig, updatedAt: number }>>({});
  const [sortBy, setSortBy] = useState<'DATE' | 'NAME'>('DATE');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [showJsonWorkshop, setShowJsonWorkshop] = useState(false);
  const [workshopJson, setWorkshopJson] = useState('');

  const { getLockInfo } = useSettingLocks('STUDIO');
  const cloudLock = getLockInfo('cloud_storage');
  const bulkLock = getLockInfo('bulk_operations');

  useEffect(() => {
    // Force LOCAL if CLOUD is locked
    if (activeTab === 'CLOUD' && cloudLock.isLocked) {
      setActiveTab('LOCAL');
    }
  }, [cloudLock.isLocked]);

  useEffect(() => {
    refreshSaves();
  }, [activeTab]);

  const refreshSaves = async () => {
    if (activeTab === 'CLOUD') {
      setIsLoading(true);
      const saves = await CloudService.getSaves();
      setCloudSaves(saves);
      setIsLoading(false);
    } else {
      // Phase 6: Load from IndexedDB
      const saves: Record<string, { config: SimulationConfig, updatedAt: number }> = {};
      
      // 1. Check for legacy localStorage saves and migrate them
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith('hypnotic_save_')) {
          try {
            const raw = safeGetItem(key)!;
            const parsed = JSON.parse(raw);
            const name = key.replace('hypnotic_save_', '');
            const data = parsed.config && parsed.updatedAt ? parsed : { config: parsed, updatedAt: Date.now() };
            
            // Migrate to IndexedDB
            await persistentStore.setJson('SAVES', `local_${name}`, data);
            // Delete from localStorage after migration
            await safeRemoveItem(key);
            i--; // Adjust index
          } catch (e) {}
        }
      }

      // 2. Load from IndexedDB
      try {
        const db = await (persistentStore as any).getDB();
        const records = await db.getAll('simulation_saves');
        records.forEach((r: any) => {
          if (r.id.startsWith('local_')) {
            const name = r.id.replace('local_', '');
            saves[name] = r.data;
          }
        });
      } catch (e) {
        console.error("Failed to load local saves from IndexedDB", e);
      }
      
      setLocalSaves(saves);
    }
  };

  const handleSave = async () => {
    const name = saveName.trim() || `Save ${new Date().toLocaleTimeString()}`;
    if (activeTab === 'CLOUD') {
      if (cloudLock.isLocked) {
        alert(cloudLock.reason);
        return;
      }
      setIsLoading(true);
      
      // 1. Check for unsaved custom melodies
      let melodyId = undefined;
      // If selected melody is custom, find it in customMelodies
      const customMelody = config.customMelodies.find(m => m.name === config.selectedMelody || m.id === config.selectedMelody);
      if (customMelody) {
          const res = await CloudService.registerMelody(customMelody.name, customMelody);
          if (res && res.id) {
              melodyId = res.id;
          }
      }

      const saved = await CloudService.saveConfig(name, config, false, melodyId);
      setIsLoading(false);
      
      if (saved) {
        setSaveName('');
        refreshSaves();
        // Maybe a small success indicator could be added here, but for now we just don't clear on failure
      } else {
        alert("Cloud save failed. Please ensure you are logged in and have a stable connection.");
      }
    } else {
      const saveData = {
        config,
        updatedAt: Date.now()
      };
      // Phase 6: Save to IndexedDB
      await persistentStore.setJson('SAVES', `local_${name}`, saveData);
      
      // Flush queued stats to backend if possible
      CloudService.flushQueuedStats();

      setSaveName('');
      refreshSaves();
    }
  };

  const loadConfig = async (savedConfig: SimulationConfig, saveId?: string) => {
    if (window.confirm("Load this configuration? Current unsaved changes will be lost.")) {
      let finalConfig = savedConfig;
      
      // Phase 5 Optimization: If config is null (not fetched yet from R2 in list view), fetch it now
      if (!finalConfig && saveId) {
          setIsLoading(true);
          const fullSave = await CloudService.getSave(saveId);
          setIsLoading(false);
          if (fullSave && fullSave.config) {
              finalConfig = fullSave.config;
          } else {
              alert("Failed to load full configuration from cloud.");
              return;
          }
      }

      if (!finalConfig) return;
      
      // If loading from cloud, we might need to fetch the linked melody/sounds
      if (activeTab === 'CLOUD' && saveId) {
          // We need to fetch the full save details again? 
          // Actually getSaves() returns the config.
          // But we modified the server to return melody_id.
          // We need to find the save object in cloudSaves to get the melody_id.
          const save = cloudSaves.find(s => s.id === saveId);
          if (save && (save as any).melodyId) {
             // Fetch melody
             const melodies = await CloudService.getMelodies();
             const melody = melodies.find(m => m.id === (save as any).melodyId);
             if (melody) {
                 // Inject into customMelodies
                 // Check if already exists
                 const exists = finalConfig.customMelodies.find(m => m.name === melody.name);
                 if (!exists) {
                     const melodyData = melody.data;
                     const newMelody: CustomMelody = {
                         id: melody.id,
                         name: melody.name,
                         frequencies: melodyData.frequencies || melodyData,
                         format: melodyData.format || 'FREQUENCIES',
                         midiData: melodyData.midiData
                     };
                     finalConfig.customMelodies.push(newMelody);
                 }
                 finalConfig.selectedMelody = melody.name;
             }
          }
      }
      
      setConfig(finalConfig);
    }
  };

  const deleteSave = async (idOrName: string) => {
    if (!window.confirm("Are you sure?")) return;
    if (activeTab === 'CLOUD') {
      await CloudService.deleteSave(idOrName);
    } else {
      await persistentStore.delete('SAVES', `local_${idOrName}`);
    }
    refreshSaves();
  };

  const setAsDefault = async (id: string) => {
    if (activeTab === 'CLOUD') {
      await CloudService.updateSave(id, { isDefault: true });
      refreshSaves(); // refresh to show update
    } else {
      // Local default is just the main storage key
      await safeSetItem(STORAGE_KEY, JSON.stringify(config));
      alert("This configuration is now your local default.");
    }
  };

  const transferToCloud = async (name: string, savedConfig: SimulationConfig) => {
    const cloudName = prompt("Enter name for Cloud Save:", name);
    if (cloudName === null) return;

    setIsLoading(true);
    try {
        const result = await CloudService.saveConfig(cloudName.trim() || name, savedConfig);
        if (result) {
            if (window.confirm(`Successfully uploaded "${cloudName}" to Cloud. Switch to Cloud tab?`)) {
                setActiveTab('CLOUD');
            }
        } else {
            alert("Upload failed. You might not be logged in or your session has expired.");
        }
    } catch (e) {
        alert("Upload failed. Please check your connection.");
    }
    setIsLoading(false);
  };

  const handleExportAll = () => {
    const data = {
      local: localSaves,
      cloud: cloudSaves,
      timestamp: Date.now(),
      version: "1.0"
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hypnotic_backup_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportAll = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        
        // Import Local
        if (data.local) {
          for (const [name, config] of Object.entries(data.local)) {
            await safeSetItem(`hypnotic_save_${name}`, JSON.stringify(config));
          }
        }

        // Import Cloud
        if (data.cloud && Array.isArray(data.cloud)) {
          if (window.confirm(`Found ${data.cloud.length} cloud saves. Upload them to cloud?`)) {
             setIsLoading(true);
             for (const save of data.cloud) {
               await CloudService.saveConfig(save.name, save.config);
             }
             setIsLoading(false);
          }
        }

        alert("Import complete!");
        refreshSaves();
      } catch (err) {
        alert("Invalid backup file.");
      }
    };
    reader.readAsText(file);
  };

  const handleWorkshopApply = async () => {
    try {
      const data = JSON.parse(workshopJson);
      
      if (data.local) {
        for (const [name, config] of Object.entries(data.local)) {
          await safeSetItem(`hypnotic_save_${name}`, JSON.stringify(config));
        }
      }

      if (data.cloud && Array.isArray(data.cloud)) {
        if (window.confirm(`Found ${data.cloud.length} cloud saves in JSON. Upload them?`)) {
          setIsLoading(true);
          for (const save of data.cloud) {
            await CloudService.saveConfig(save.name, save.config);
          }
          setIsLoading(false);
        }
      }

      alert("Workshop data applied successfully!");
      refreshSaves();
      setShowJsonWorkshop(false);
    } catch (e) {
      alert("Invalid JSON format in workshop.");
    }
  };

  const openWorkshop = () => {
    const data = {
      local: localSaves,
      cloud: cloudSaves,
      timestamp: Date.now()
    };
    setWorkshopJson(JSON.stringify(data, null, 2));
    setShowJsonWorkshop(true);
  };

  const handleClearAllLocal = async () => {
    if (window.confirm("CRITICAL: This will delete ALL local snapshots and reset the Creator Studio to factory defaults. This cannot be undone. Proceed?")) {
      // 1. Clear IndexedDB
      await persistentStore.clear();
      
      // 2. Clear localStorage
      window.localStorage.clear();
      
      // 3. Reset application state
      onReset();
      
      // 4. Refresh list
      refreshSaves();
      
      alert("Local storage and IndexedDB cleared.");
    }
  };

  const sortedLocalSaves = Object.entries(localSaves).sort((a, b) => {
    const [nameA, dataA] = a;
    const [nameB, dataB] = b;
    let comparison = 0;
    if (sortBy === 'DATE') {
      comparison = dataA.updatedAt - dataB.updatedAt;
    } else {
      comparison = nameA.localeCompare(nameB);
    }
    return sortOrder === 'ASC' ? comparison : -comparison;
  });

  const sortedCloudSaves = [...cloudSaves].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'DATE') {
      comparison = a.updatedAt - b.updatedAt;
    } else {
      comparison = a.name.localeCompare(b.name);
    }
    return sortOrder === 'ASC' ? comparison : -comparison;
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between px-1 mb-2">
        <SectionLabel icon="fa-database" darkMode={config.darkMode}>Save System</SectionLabel>
        {/* Potentially an info icon here for overall save system but SectionLabel might not support it easily */}
      </div>
      
      <ControlGroup label="Select Storage" settingKey="storageTab" darkMode={config.darkMode}>
        <div className={`grid grid-cols-2 gap-1 p-1 rounded-xl w-full ${config.darkMode ? 'bg-neutral-800' : 'bg-neutral-100'}`}>
          <button onClick={() => setActiveTab('LOCAL')} className={`text-[9px] font-black py-2 rounded-lg transition-all ${activeTab === 'LOCAL' ? 'bg-cyan-600 text-white shadow-sm' : (config.darkMode ? 'text-neutral-500' : 'text-neutral-400')}`}>Local</button>
          <button 
            onClick={() => !cloudLock.isLocked && setActiveTab('CLOUD')} 
            title={cloudLock.isLocked ? cloudLock.reason : 'Cloud Storage'}
            className={`text-[9px] font-black py-2 rounded-lg transition-all flex items-center justify-center gap-1 ${activeTab === 'CLOUD' ? 'bg-cyan-600 text-white shadow-sm' : (config.darkMode ? 'text-neutral-500' : 'text-neutral-400')} ${cloudLock.isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {cloudLock.isLocked && <i className="fa-solid fa-lock text-[7px]"></i>}
            Cloud
          </button>
        </div>
      </ControlGroup>

      <div className="flex flex-col gap-2 mb-2">
        <ControlGroup label="Save Name" settingKey="saveName" darkMode={config.darkMode}>
          <div className="flex gap-2 w-full">
            <input 
              type="text" 
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="Snapshot name..."
              className={`flex-1 px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            />
            <button 
              onClick={() => {
                if (activeTab === 'CLOUD' && cloudLock.isLocked) return;
                handleSave();
              }} 
              className={`px-4 text-white rounded-xl font-black text-[9px] uppercase shadow-lg transition-all ${
                activeTab === 'CLOUD' && cloudLock.isLocked 
                ? 'bg-neutral-500 cursor-not-allowed opacity-50' 
                : 'bg-cyan-600 hover:bg-cyan-700 shadow-cyan-500/20'
              }`}
            >
              {activeTab === 'CLOUD' && cloudLock.isLocked ? <i className="fa-solid fa-lock mr-2"></i> : null}
              Save
            </button>
          </div>
        </ControlGroup>

        <div className="flex items-center justify-between px-1">
          <div className="flex gap-2">
            <button 
              onClick={() => setSortBy(sortBy === 'DATE' ? 'NAME' : 'DATE')}
              className={`text-[8px] font-black uppercase transition-all ${config.darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}
            >
              Sort: {sortBy}
            </button>
            <button 
              onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              className={`text-[8px] font-black uppercase transition-all ${config.darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}
            >
              Order: {sortOrder}
            </button>
          </div>
          <span className={`text-[7px] uppercase font-black ${config.darkMode ? 'opacity-30' : 'text-neutral-900/40'}`}>
            {activeTab === 'LOCAL' ? Object.keys(localSaves).length : cloudSaves.length} SAVES
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2 max-h-60 overflow-y-auto custom-scrollbar">
        {isLoading && <div className="text-center text-[10px] opacity-50 py-4">Connecting to Cloud...</div>}
        
        {activeTab === 'LOCAL' && sortedLocalSaves.length === 0 && (
          <div className="text-center text-[10px] opacity-50 py-4">No local saves found.</div>
        )}
        
        {activeTab === 'LOCAL' && sortedLocalSaves.map(([name, data]) => (
          <div key={name} className={`flex items-center justify-between p-2 rounded-lg border transition-all ${config.darkMode ? 'bg-neutral-800/40 border-neutral-700' : 'bg-white border-neutral-200'} min-w-0`}>
             <div className="flex flex-col min-w-0 flex-1 mr-2">
                <span className={`text-[10px] font-black truncate ${config.darkMode ? 'text-white' : 'text-neutral-900'}`} title={name}>{name}</span>
                <span className={`text-[7px] ${config.darkMode ? 'opacity-40' : 'text-neutral-900/50'}`}>{new Date(data.updatedAt).toLocaleDateString()}</span>
             </div>
             <div className="flex gap-1 shrink-0">
               <button onClick={() => loadConfig(data.config as SimulationConfig)} title="Load" className="px-2 py-1 bg-green-500/10 text-green-500 rounded text-[9px] font-black hover:bg-green-500/20">LOAD</button>
               <button 
                 onClick={() => !cloudLock.isLocked && transferToCloud(name, data.config as SimulationConfig)} 
                 title={cloudLock.isLocked ? cloudLock.reason : "Upload to Cloud"} 
                 className={`px-2 py-1 rounded text-[9px] transition-all flex items-center justify-center ${cloudLock.isLocked ? 'bg-neutral-500/10 text-neutral-500 cursor-not-allowed opacity-50' : 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20'}`}
               >
                 {cloudLock.isLocked ? <i className="fa-solid fa-lock text-[8px]"></i> : <i className="fa-solid fa-cloud-arrow-up"></i>}
               </button>
               <button onClick={() => deleteSave(name)} title="Delete" className="px-2 py-1 bg-red-500/10 text-red-500 rounded text-[9px] hover:bg-red-500/20"><i className="fa-solid fa-trash"></i></button>
             </div>
          </div>
        ))}

        {activeTab === 'CLOUD' && sortedCloudSaves.map(save => (
          <div key={save.id} className={`flex items-center justify-between p-2 rounded-lg border transition-all ${save.isDefault ? 'border-yellow-500/50 bg-yellow-500/5' : (config.darkMode ? 'bg-neutral-800/40 border-neutral-700' : 'bg-white border-neutral-200')} min-w-0`}>
             <div className="flex flex-col min-w-0 flex-1 mr-2">
               <span className={`text-[10px] font-black truncate flex items-center gap-2 ${config.darkMode ? 'text-white' : 'text-neutral-900'}`} title={save.name}>
                 {save.name}
                 {save.isDefault && <i className="fa-solid fa-star text-yellow-500 text-[8px] shrink-0"></i>}
               </span>
               <span className={`text-[7px] ${config.darkMode ? 'opacity-40' : 'text-neutral-900/50'}`}>{new Date(save.updatedAt).toLocaleDateString()}</span>
             </div>
             <div className="flex gap-1 shrink-0">
               <button onClick={() => loadConfig(save.config as SimulationConfig, save.id)} title="Load" className="px-2 py-1 bg-green-500/10 text-green-500 rounded text-[9px] font-black hover:bg-green-500/20">LOAD</button>
               <button onClick={() => setAsDefault(save.id)} title="Set as Cloud Default" className={`px-2 py-1 rounded text-[9px] hover:bg-yellow-500/20 ${save.isDefault ? 'bg-yellow-500 text-black' : 'bg-yellow-500/10 text-yellow-500'}`}><i className="fa-solid fa-star"></i></button>
               <button onClick={() => deleteSave(save.id)} title="Delete" className="px-2 py-1 bg-red-500/10 text-red-500 rounded text-[9px] hover:bg-red-500/20"><i className="fa-solid fa-trash"></i></button>
             </div>
          </div>
        ))}
      </div>
      
      {activeTab === 'LOCAL' && (
        <button onClick={() => setAsDefault('')} className="mt-2 text-[8px] opacity-50 hover:opacity-100 uppercase font-black text-center w-full">Set Current as Auto-Load Default</button>
      )}

      <div className={`mt-4 pt-4 border-t flex flex-col gap-2 ${config.darkMode ? 'border-neutral-800' : 'border-neutral-100'}`}>
        <div className="flex items-center justify-between mb-1">
            <div className={`flex items-center gap-2 ${bulkLock.isLocked ? 'opacity-60 grayscale' : ''}`}>
                <span className="text-[8px] uppercase font-black opacity-40">Bulk Operations</span>
                {bulkLock.isLocked && <i className="fa-solid fa-lock text-[7px] text-cyan-500"></i>}
                <InfoPopup content={bulkLock.isLocked ? `Locked: ${bulkLock.reason}` : "Export all your saves to a JSON file or import them from a backup. Use JSON Workshop for advanced editing."} darkMode={config.darkMode} />
            </div>
            {!bulkLock.isLocked && <button onClick={openWorkshop} className="text-[8px] font-black text-cyan-500 hover:underline uppercase">JSON Workshop</button>}
        </div>
        
        {showJsonWorkshop && !bulkLock.isLocked && (
          <div className={`flex flex-col gap-2 p-3 rounded-2xl border mb-2 ${config.darkMode ? 'bg-neutral-900 border-neutral-700' : 'bg-neutral-50 border-neutral-200'}`}>
            <SectionLabel icon="fa-code" darkMode={config.darkMode}>JSON Workshop</SectionLabel>
            <textarea 
              value={workshopJson}
              onChange={e => setWorkshopJson(e.target.value)}
              className={`w-full h-40 p-2 rounded-xl text-[8px] font-mono outline-none border ${config.darkMode ? 'bg-black border-neutral-800 text-cyan-400' : 'bg-white border-neutral-200 text-cyan-700'}`}
              placeholder="Paste your backup JSON here..."
            />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setShowJsonWorkshop(false)} className="py-2 rounded-lg text-[9px] font-black uppercase bg-neutral-500/10 text-neutral-500">Cancel</button>
              <button onClick={handleWorkshopApply} className="py-2 rounded-lg text-[9px] font-black uppercase bg-cyan-600 text-white shadow-lg shadow-cyan-500/20">Apply JSON</button>
            </div>
          </div>
        )}

        <div className={`grid grid-cols-2 gap-2 ${bulkLock.isLocked ? 'opacity-60 pointer-events-none grayscale' : ''}`}>
          <ControlGroup label="Export" settingKey="exportAll" darkMode={config.darkMode}>
            <button 
              onClick={handleExportAll}
              className={`flex items-center justify-center gap-2 py-2 w-full rounded-xl text-[9px] font-black uppercase transition-all ${config.darkMode ? 'bg-neutral-800 hover:bg-neutral-700 text-white' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-800'}`}
            >
              <i className="fa-solid fa-download"></i>
              Export
            </button>
          </ControlGroup>
          <ControlGroup label="Import" settingKey="importAll" darkMode={config.darkMode}>
            <label className={`flex items-center justify-center gap-2 py-2 w-full rounded-xl text-[9px] font-black uppercase cursor-pointer transition-all ${config.darkMode ? 'bg-neutral-800 hover:bg-neutral-700 text-white' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-800'}`}>
              <i className="fa-solid fa-upload"></i>
              Import
              <input type="file" accept=".json" onChange={handleImportAll} className="hidden" />
            </label>
          </ControlGroup>
        </div>

        {activeTab === 'LOCAL' && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button 
              onClick={onReset}
              className="py-3 rounded-2xl border border-zinc-500/10 bg-black/5 text-zinc-500 text-[9px] font-black uppercase hover:bg-black/10 transition-all flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset to Default
            </button>
            <button 
                onClick={handleClearAllLocal}
                className="py-3 rounded-2xl border border-red-500/20 bg-red-500/5 text-red-500 text-[9px] font-black uppercase hover:bg-red-500/10 transition-all flex items-center justify-center gap-2"
            >
                <i className="fa-solid fa-trash-can"></i>
                Clear Local Saves
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
